import { forwardRef, Ref, RefAttributes, type JSX, useCallback } from "react";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FieldValues, useController, useWatch } from "react-hook-form";

import { BaseFormCheckboxProps, FormCheckboxProps, SubscribedFormCheckboxProps } from "./types";

import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox/DashboardCheckbox";
import { FormField } from "@/forms-design-system/FormField/FormField";
import { FormFieldLabel } from "@/forms-design-system/FormField/FormFieldLabel";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { mergeSx } from "@/forms-design-system/helpers";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { useValidators } from "@/hooks/useValidators";
import { Check, Minus } from "@/icons/outlined";

function FormCheckboxInner<T extends FieldValues>(
  { control, rules, name, shouldUnregister, ...rest }: FormCheckboxProps<T>,
  ref: Ref<HTMLDivElement>
) {
  return control && name ? (
    <SubscribedFormCheckbox
      {...rest}
      ref={ref}
      control={control}
      rules={rules}
      name={name}
      shouldUnregister={shouldUnregister}
    />
  ) : (
    <BaseFormCheckbox {...rest} name={name} ref={ref} />
  );
}

FormCheckboxInner.displayName = "FormCheckbox";

export const FormCheckbox = forwardRef(FormCheckboxInner) as <T extends FieldValues>(
  props: FormCheckboxProps<T> & RefAttributes<HTMLDivElement>
) => JSX.Element;

function SubscribedFormCheckboxInner<T extends FieldValues>(
  {
    control,
    defaultChecked,
    name,
    onBlur,
    onChange,
    rules,
    shouldUnregister,
    inputRef,
    ...rest
  }: SubscribedFormCheckboxProps<T>,
  ref: Ref<HTMLDivElement>
) {
  const validators = useValidators(rules);
  const watchedValue = useWatch({ control, name });
  const internalDefaultValue = defaultChecked || watchedValue;

  const { field } = useController<T>({
    name,
    control,
    defaultValue: internalDefaultValue as never,
    rules: validators,
    shouldUnregister,
  });

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onChange?.(event, checked);
      field.onChange(checked);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      field.onBlur();
    },
    [field, onBlur]
  );

  return (
    <BaseFormCheckbox
      {...rest}
      checked={Boolean(field.value)}
      inputRef={inputRef}
      ref={ref}
      onBlur={handleBlur}
      onChange={handleChange}
      data-test={rest?.["data-test"] || "form-checkbox"}
      name={name}
    />
  );
}

const SubscribedFormCheckbox = forwardRef(SubscribedFormCheckboxInner) as <T extends FieldValues>(
  props: SubscribedFormCheckboxProps<T> & RefAttributes<HTMLDivElement>
) => JSX.Element;

const BaseFormCheckbox = forwardRef<HTMLDivElement, BaseFormCheckboxProps>(
  (
    {
      id,
      inputRef,
      size = "medium",
      error,
      disabled,
      required,
      label,
      helperText,
      infoTooltip,
      components,
      checked,
      defaultChecked,
      indeterminate = false,
      sx,
      "data-test": dataTest,
      onBlur,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents.checkbox) {
      return <InvalidThemeErrorMessage />;
    }

    const iconStyles = {
      height: "100%",
      width: "100%",
      borderRadius: "4px",
      boxSizing: "border-box" as const,
    };

    const uncheckedIconStyles = {
      ...iconStyles,
      color: theme.palette.formsComponents.checkbox.backgroundColor,
      border: `1px solid ${theme.palette.formsComponents.checkbox.borderColor}`,
      backgroundColor: theme.palette.formsComponents.checkbox.backgroundColor,
      ...(error && {
        borderColor: theme.palette.error.main,
      }),
      ...components?.uncheckedIcon?.sx,
    };

    const checkedIconStyles = {
      ...iconStyles,
      backgroundColor: theme.palette.formsComponents.checkbox.fill,
      color: theme.palette.formsComponents.checkbox.iconColor,
      border: `1px solid ${theme.palette.formsComponents.checkbox.fill}`,
      ...components?.checkedIcon?.sx,
    };

    const checkboxStyles = mergeSx(
      {
        transition: "background-color 200ms ease-out",
        "& .MuiBox-root": {
          outline: "0px solid transparent",
          transition: "outline 100ms ease-in-out, border-color 200ms ease-in-out",
        },
        "&:hover .MuiBox-root": {
          border: `1px solid ${theme.palette.formsComponents.checkbox.hoverBorderColor}`,
        },
        "& .MuiButtonBase-root.MuiCheckbox-root:focus-within ~ .MuiBox-root, &:focus-within .MuiBox-root": {
          outline: `2px solid ${theme.palette.formsComponents.checkbox.focusOutlineColor}`, // outline for checkbox is only 2px due to its small size
        },
      },
      components?.checkbox?.sx
    );

    const emptyIconElement = <Box sx={uncheckedIconStyles} />;
    const checkedIconElement = (
      <FormIcon size={size} sx={checkedIconStyles}>
        <Check />
      </FormIcon>
    );
    const indeterminateIconElement = (
      <FormIcon size={size} sx={checkedIconStyles}>
        <Minus />
      </FormIcon>
    );

    const inputId = rest.name;
    const labelWithInfo = (
      <FormFieldLabel
        htmlFor={inputId}
        label={label}
        required={required}
        infoTooltip={infoTooltip}
        disabled={disabled}
        size={size}
        sx={{ display: "inline-flex" }}
        components={{
          label: {
            sx: {
              cursor: disabled ? "default" : "pointer",
            },
          },
        }}
      />
    );

    return (
      <FormField
        ref={ref}
        id={id}
        helperText={helperText}
        error={error}
        required={required}
        sx={sx}
        data-test={dataTest}
      >
        <DashboardCheckbox
          inputRef={inputRef}
          size={size}
          sx={checkboxStyles}
          disabled={disabled}
          {...(checked !== undefined ? { checked } : { defaultChecked })}
          checkedIcon={checkedIconElement}
          icon={emptyIconElement}
          indeterminate={indeterminate}
          indeterminateIcon={indeterminateIconElement}
          label={labelWithInfo}
          onBlur={onBlur}
          {...rest}
        />
      </FormField>
    );
  }
);

BaseFormCheckbox.displayName = "BaseFormCheckbox";
